import { Map } from "lucide-react";
import { Card } from "../../components/tailwind/card";
import { usePageTitle } from "../../hooks/usePageTitle";
import { WorkflowAnalyticsDailyBreakdown } from "./components/WorkFlow/analytics/WorkflowAnalyticsDailyBreakdown";
import { WorkflowAnalyticsFilters } from "./components/WorkFlow/analytics/WorkflowAnalyticsFilters";
import { WorkflowAnalyticsFunnel } from "./components/WorkFlow/analytics/WorkflowAnalyticsFunnel";
import { WorkflowAnalyticsHeader } from "./components/WorkFlow/analytics/WorkflowAnalyticsHeader";
import { WorkflowAnalyticsHeatmap } from "./components/WorkFlow/analytics/WorkflowAnalyticsHeatmap";
import { WorkflowAnalyticsInsightsCard } from "./components/WorkFlow/analytics/WorkflowAnalyticsInsightsCard";
import { WorkflowAnalyticsMetrics } from "./components/WorkFlow/analytics/WorkflowAnalyticsMetrics";
import { useWorkflowAnalyticsPage } from "./hooks/useWorkflowAnalyticsPage";
import { workflowTheme } from "./utils/workflowBuilderTheme";
import { WA_PAGE_PAD, waShell } from "./utils/whatsappPageShell";
import { AnalyticsSkeleton } from "./components/skeletons/WhatsAppPageSkeletons";

export default function WorkflowAnalytics() {
  const {
    workflows,
    workflowId,
    setWorkflowId,
    version,
    setVersion,
    month,
    setMonth,
    versionOptions,
    listLoading,
    isLoading,
    error,
    hasData,
    workflowName,
    monthLabel,
    liveSessionCount,
    metrics,
    nodeRows,
    heatMapRows,
    dailyRows,
    insightMessage
  } = useWorkflowAnalyticsPage();

  usePageTitle(workflowName ? `${workflowName} — Analytics` : "Workflow Analytics");

  return (
    <div className={waShell("w-full", WA_PAGE_PAD, "space-y-5", "animate-fade-in", workflowTheme.page)}>
      <div className="animate-fade-in-up space-y-5">
        <WorkflowAnalyticsHeader
          monthLabel={monthLabel}
          liveSessionCount={liveSessionCount}
        />

        <WorkflowAnalyticsFilters
          workflows={workflows}
          workflowId={workflowId}
          onWorkflowIdChange={setWorkflowId}
          version={version}
          onVersionChange={setVersion}
          versionOptions={versionOptions}
          month={month}
          onMonthChange={setMonth}
          listLoading={listLoading}
        />

        {error && (
          <div className={`rounded-xl border p-4 ${workflowTheme.errorBanner}`}>
            <p className={`text-sm font-medium ${workflowTheme.errorTitle}`}>Could not load analytics</p>
            <p className={`text-sm mt-1 ${workflowTheme.errorText}`}>{error}</p>
          </div>
        )}

        {isLoading && (
          <AnalyticsSkeleton embedded />
        )}

        {!isLoading && !error && !hasData && (
          <Card className={`p-12 text-center ${workflowTheme.card}`}>
            <Map className={`w-12 h-12 mx-auto mb-4 ${workflowTheme.textMuted}`} />
            <h2 className={`text-lg font-semibold ${workflowTheme.textPrimary}`}>No analytics yet</h2>
            <p className={`text-sm mt-2 max-w-md mx-auto ${workflowTheme.textSecondary}`}>
              Run this workflow with live sessions to see outcomes, node performance, and live occupancy here.
            </p>
          </Card>
        )}

        {!isLoading && hasData && (
          <>
            {metrics && <WorkflowAnalyticsMetrics metrics={metrics} />}

            {insightMessage && (
              <WorkflowAnalyticsInsightsCard
                message={insightMessage}
                workflowId={workflowId}
                version={version}
              />
            )}

            {nodeRows.length > 0 && <WorkflowAnalyticsFunnel nodeRows={nodeRows} />}

            <div
              className={`grid grid-cols-1 gap-5 ${
                dailyRows.length > 0 ? "lg:grid-cols-3" : ""
              }`}
            >
              {dailyRows.length > 0 && (
                <div className="lg:col-span-2">
                  <WorkflowAnalyticsDailyBreakdown dailyRows={dailyRows} />
                </div>
              )}
              <WorkflowAnalyticsHeatmap
                heatMapRows={heatMapRows}
                liveSessionCount={liveSessionCount}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
